'use client'

import { Button } from '@/presentation/components/button'
import { cn } from '@/presentation/utils'
import { MinusIcon, PlusIcon } from '@radix-ui/react-icons'
import range from 'lodash/range'
import uniqueId from 'lodash/uniqueId'
import { useEffect, useReducer } from 'react'
import { AutoForm, type AutoFormSchema, type AutoFormWidgetsProps } from '../autoForm'
import { Field } from '../field'
import { FieldLabel } from '../label'
import { FieldMessage } from '../message'

type ArrayWidgetProps = AutoFormWidgetsProps

type ActionType =
  | {
      type: 'append'
      payload: { key: string; value: number }
    }
  | {
      type: 'remove'
      payload: string
    }
  | {
      type: 'hydrate'
      payload: Record<string, number>
    }

function reducer(state: Record<string, number>, action: ActionType) {
  switch (action.type) {
    case 'append': {
      return { ...state, [action.payload.key]: action.payload.value }
    }

    case 'remove': {
      const { [action.payload]: _, ...rest } = state
      return rest
    }

    case 'hydrate':
      return action.payload

    default:
      return state
  }
}

export function ArrayWidget(props: ArrayWidgetProps) {
  const [items, dispatch] = useReducer(reducer, {})

  useEffect(() => {
    if (props.initialValue) {
      const initial = props.initialValue as (string | number | Record<string, unknown>)[]
      const payload = range(initial.length).reduce(
        (agg, item) => {
          const newAgg = structuredClone(agg)
          newAgg[uniqueId(props.path)] = item

          return newAgg
        },
        {} as Record<string, number>,
      )

      dispatch({ type: 'hydrate', payload })
    }
  }, [props.path, props.initialValue])

  const initialValues = (props.initialValue as Record<string, unknown>[]) || {}

  return (
    <Field path={props.path} className={cn('pb-2', props.classNames?.root)}>
      <FieldLabel>{props.options?.label as string}</FieldLabel>
      <p className="text-xs">{props.options?.description as string}</p>
      <FieldMessage />

      <div className="divide-y mb-4">
        {Object.keys(items).map((key, idx) => (
          <div key={key} className="flex justify-between items-end py-3 gap-2">
            <div className={cn('flex-1 flex flex-wrap', props.classNames?.container)}>
              <AutoForm
                prefix={`${props.path}.${idx}`}
                schema={props.options?.elements as AutoFormSchema}
                initialValues={initialValues[items[key]]}
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="icon"
              title="remove item"
              onClick={() => dispatch({ type: 'remove', payload: key })}
            >
              <MinusIcon className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>

      <Button
        variant="outline"
        type="button"
        title="Add item"
        onClick={() =>
          dispatch({
            type: 'append',
            payload: { key: uniqueId(props.path), value: Object.keys(items).length },
          })
        }
      >
        <PlusIcon className="h-4 w-4" />
      </Button>
    </Field>
  )
}
